// src/hazir-beton/src/pages/SevkiyatDuzeltme.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

const INOSEL_ID = "27517";



const formatPlate = (raw) => {
  const t = String(raw ?? "").replace(/\s+/g, "").toUpperCase();
  return t.replace(/[^A-Z0-9]/g, "");

};


/* ===================== HELPERS ===================== */

const asText = (v) => String(v ?? "").trim();

const normPlateKey = (s) =>
  asText(s)
    .toUpperCase()
    .replace(/\s+/g, "")
    .replaceAll("İ", "I")
    .replaceAll("ı", "I");

const safeNum = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const formatTimeSmart = (v) => {
  const d = String(v ?? "").replace(/\D/g, "");
  if (!d) return "";
  const s = d.length >= 4 ? d.slice(-4) : d.padStart(4, "0");
  return `${s.slice(0, 2)}:${s.slice(2, 4)}`;
};

const parseHHMM = (hhmm) => {
  const t = String(hhmm ?? "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// irsaliye no sonundaki rakamlar (sıralama için basit yaklaşım)
const irsSuffixNum = (irs) => {
  const s = asText(irs);
  const m = s.match(/(\d+)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
};

// arrayUnion içinde serverTimestamp kullanmıyoruz (DolumDuzeltSil mantığı) 
const historyEntry = ({ action, by, changes }) => ({
  action,
  by: by || "anonim",
  at: Timestamp.now(),
  changes: changes || {},
});

const diffChanges = (before, after, keys) => {
  const out = {};
  for (const k of keys) {
    const be = before?.[k] ?? "";
    const af = after?.[k] ?? "";
    if (String(be) !== String(af)) out[k] = [be, af];
  }
  return out;
};

const hasChangeDetail = (changes) => !!changes && Object.keys(changes).length > 0;

const isRowLockedGeneric = (data, currentEmail) => {
  // Kullanıcı “lock:true” dediği için her ihtimale karşı birkaç formatı da yakalıyoruz.
  if (data?.lock === true) return true;
  if (data?.lock?.active === true && asText(data.lock.by) && data.lock.by !== currentEmail)
    return true;
  if (data?.lock?.isOpen === true && asText(data.lock.openedBy) && data.lock.openedBy !== currentEmail)
    return true;
  return false;
};

const formatLockTime = (value) => {
  if (!value) return "";
  try {
    let d = null;
    if (typeof value?.toDate === "function") d = value.toDate();
    else if (typeof value?.seconds === "number") d = new Date(value.seconds * 1000);
    else d = new Date(value);
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleString("tr-TR") : "";
  } catch {
    return "";
  }
};

const lockReasonText = (data, currentEmail) => {
  const lock = data?.lock;
  if (lock === true) return "Kilitli: kayıt başka bir işlem tarafından açık görünüyor.";

  const by = asText(lock?.openedBy || lock?.by);
  const at = lock?.openedAt || lock?.at;
  const timeText = formatLockTime(at);
  const relation = by && by === currentEmail ? "siz" : by || "bilinmeyen kullanıcı";

  if (lock?.isOpen === true || lock?.active === true) {
    return `Kilitli: ${relation}${timeText ? " - " + timeText : ""} tarihinde açtı.`;
  }

  return "Kilit yok.";
};

/* ===================== COMPONENT ===================== */

/* ============= MODERN TASARIM STİLLERİ ============= */
const modernInput = {
  height: "42px",
  padding: "0 12px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  fontSize: "15px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  backgroundColor: "#ffffff"
};

const cardStyle = {
  background: "#ffffff",
  padding: "20px",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  marginTop: "20px"
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: "700",
  color: "#475569",
  marginBottom: "6px",
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.5px"
};

export default function SevkiyatDuzeltme() {
  const personeller = usePersonelList(); // Bu satır eksik olduğu için hata alıyorsun
  const email = auth.currentUser?.email || "";

  const [mode, setMode] = useState("mikser"); // mikser | pompa
  const [searchType, setSearchType] = useState("irsaliye"); // irsaliye | plakaTarih

  const [irsaliyeNo, setIrsaliyeNo] = useState("");
  const [tarihStr, setTarihStr] = useState(todayISO());
  const [plaka, setPlaka] = useState("");

  const [platesAll, setPlatesAll] = useState([]);
  const [loading, setLoading] = useState(false);



  // Bulunan kayıtlar (plaka+tarih ile birden fazla dönebilir)
  const [results, setResults] = useState([]);
  // Seçilen kayıt(lar) (mikserde 1 doc; pompada group düzeni için aynı gün aynı pompa+şantiye seti)
  const [selectedDocs, setSelectedDocs] = useState([]);

  // Pompa için group state (baslangic/bitis, operator/yagci listeleri)
  const [pompaGroup, setPompaGroup] = useState({
    baslangicSaat: "",
    bitisSaat: "",
    operatorList: [""],
    yagciList: [""],
  });

  // Kilit (pompa group lock)
  const [activePompaGroupKey, setActivePompaGroupKey] = useState(null);
  const pompaLockedRef = useRef(false);
  const spaceShortcutRef = useRef(false);
  const runSearchRef = useRef(null);
  const handleSaveRef = useRef(null);

  /* ---- plates ---- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "plates")));
        const list = snap.docs
          .map((d) => d.data() || {})
          .filter((x) => x?.plate)
          .map((x) => ({
            plate: String(x.plate).toUpperCase(),
            key: normPlateKey(x.plate),
            // Eğer plates dokümanında tip/tür alanı varsa filtrelemek için kullanırız.
            // Yoksa boş kalır ve filtre uygulanmaz.
            type: asText(x.type || x.tur || x.vehicleType || ""),
          }));
        setPlatesAll(list);
      } catch {
        setPlatesAll([]);
      }
    })();
  }, []);

  const plateOptions = useMemo(() => {
    // İstenen: Pompa seçilirse sadece pompa plakaları; mikserde sadece mikser plakaları.
    // Ancak plates koleksiyonunda tip alanı yoksa filtreleyemeyiz; o durumda hepsini gösterir.
    const want = mode === "pompa" ? "POMPA" : "MIKSER";
    const hasType = platesAll.some((p) => p.type);
    if (!hasType) return platesAll.map((p) => p.plate).sort((a, b) => a.localeCompare(b, "tr"));
    return platesAll
      .filter((p) => asText(p.type).toUpperCase().includes(want))
      .map((p) => p.plate)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [platesAll, mode]);

  /* ===================== SEARCH ===================== */

  const resetSelection = () => {
    setResults([]);
    setSelectedDocs([]);
    setPompaGroup({ baslangicSaat: "", bitisSaat: "", operatorList: [""], yagciList: [""] });
    // pompa lock bırak
    if (activePompaGroupKey) releasePompaGroupLock(activePompaGroupKey).catch(() => {});
    setActivePompaGroupKey(null);
    pompaLockedRef.current = false;
  };

  useEffect(() => {
    resetSelection();
    // eslint-disable-next-line
  }, [mode, searchType]);

  const targetCollection = mode === "mikser" ? "hazirbeton_mikser_sevkiyat" : "hazirbeton_pompa_hakedis";

  const runSearch = async () => {
    setLoading(true);
    try {
      resetSelection();

      const col = collection(db, targetCollection);

      let q;
      if (searchType === "irsaliye") {
        const irs = asText(irsaliyeNo).toUpperCase();
        if (!irs) {
          alert("İrsaliye No giriniz.");
          return;
        }

        // Pompa/Mikser fark etmeksizin irsaliyeNo ile arıyoruz
        q = query(col, where("deleted", "==", false), where("irsaliyeNo", "==", irs));
      } else {
        // plaka + tarih
        const p = asText(plaka).toUpperCase();
        if (!p) {
          alert("Plaka giriniz.");
          return;
        }
        if (!tarihStr) {
          alert("Tarih seçiniz.");
          return;
        }

        if (mode === "mikser") {
          q = query(
            col,
            where("deleted", "==", false),
            where("tarihStr", "==", tarihStr),
            where("mikserPlaka", "==", p),
            where("mikserFirmaId", "==", INOSEL_ID)
          );
        } else {
          q = query(
            col,
            where("deleted", "==", false),
            where("tarihStr", "==", tarihStr),
            where("pompaPlaka", "==", p),
            where("pompaFirmaId", "==", INOSEL_ID)
          );
        }
      }

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

      if (!list.length) {
        alert("Kayıt bulunamadı.");
        return;
      }

      // sonuçları ekranda listeleyelim
      // (irsaliye ile bile birden fazla çıkabilir; seçtireceğiz)
      setResults(list);
    } finally {
      setLoading(false);
    }
  };

  /* ===================== SELECT ===================== */

  const acquireMikserRowLockIfNeeded = async (docId) => {
    // CarsafSevkiyat: row bazlı lock.isOpen + openedBy/openedAt 
    if (!docId || !email) return true;

    try {
      await runTransaction(db, async (tx) => {
        const ref = doc(db, "hazirbeton_mikser_sevkiyat", docId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("NOT_FOUND");
        const data = snap.data() || {};
        const lock = data.lock || {};
        const openedBy = asText(lock.openedBy);

        // kullanıcı "lock:true" dediği varyant
        if (data.lock === true) throw new Error("LOCK_TRUE");

        if (lock.isOpen === true && openedBy && openedBy !== email) {
          throw new Error("LOCKED_BY_OTHER");
        }

        tx.update(ref, {
          lock: {
            isOpen: true,
            openedBy: email,
            openedAt: serverTimestamp(),
          },
        });
      });
      return true;
    } catch (e) {
      alert("Bu kayıt şu anda başka kullanıcı tarafından düzenleniyor (kilitli).");
      return false;
    }
  };

  const releaseMikserRowLock = async (docId) => {
    if (!docId || !email) return;
    try {
      await updateDoc(doc(db, "hazirbeton_mikser_sevkiyat", docId), {
        lock: { isOpen: false, openedBy: "", openedAt: null },
      });
    } catch {}
  };

  const pompaGroupKeyOfDocs = (docs) => {
    // CarsafPompa’daki gibi: normKey(pompaPlaka) + "__" + santiyeAdi 
    const first = docs?.[0];
    if (!first) return "";
    return `${normPlateKey(first.pompaPlaka)}__${asText(first.santiyeAdi)}`;
  };

  const queryPompaGroupDocs = async (docs) => {
    const first = docs?.[0];
    if (!first) return [];
    const snap = await getDocs(
      query(
        collection(db, "hazirbeton_pompa_hakedis"),
        where("pompaFirmaId", "==", INOSEL_ID),
        where("deleted", "==", false),
        where("tarihStr", "==", first.tarihStr),
        where("pompaPlaka", "==", first.pompaPlaka),
        where("santiyeAdi", "==", first.santiyeAdi)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, data: d.data() || {} }));
  };

  const setPompaGroupLock = async (docs, active) => {
    // CarsafPompa group lock mantığı 
    if (!docs?.length) return false;
    if (!email) return false;

    const groupDocs = await queryPompaGroupDocs(docs);

    if (active) {
      const other = groupDocs.find(
        (x) => x.data?.lock?.active && x.data?.lock?.by && x.data.lock.by !== email
      );
      if (other) {
        alert(`Bu grup şu anda kilitli: ${other.data.lock.by}`);
        return false;
      }
    }

    const batch = writeBatch(db);
    for (const x of groupDocs) {
      const ref = doc(db, "hazirbeton_pompa_hakedis", x.id);
      if (active) {
        batch.update(ref, {
          lock: { active: true, by: email, at: serverTimestamp() },
        });
      } else {
        batch.update(ref, { lock: deleteField() });
      }
    }
    await batch.commit();
    return true;
  };

  const acquirePompaGroupLock = async (docs) => {
    const gk = pompaGroupKeyOfDocs(docs);
    if (!gk) return false;

    // zaten aynı group kilitliyse yeniden deneme
    if (activePompaGroupKey === gk && pompaLockedRef.current) return true;

    // farklı group ise eskisini bırak
    if (activePompaGroupKey && activePompaGroupKey !== gk) {
      await releasePompaGroupLock(activePompaGroupKey);
      setActivePompaGroupKey(null);
      pompaLockedRef.current = false;
    }

    const ok = await setPompaGroupLock(docs, true);
    if (ok) {
      setActivePompaGroupKey(gk);
      pompaLockedRef.current = true;
    }
    return ok;
  };

  const releasePompaGroupLock = async (gk) => {
    if (!gk) return;
    try {
      if (selectedDocs?.length) await setPompaGroupLock(selectedDocs, false);
    } catch {}
  };

  // sayfadan çıkarken pompa lock bırak
  useEffect(() => {
    return () => {
      if (activePompaGroupKey) {
        setPompaGroupLock(selectedDocs, false).catch(() => {});
      }
      // mikser lock bırak
      if (mode === "mikser" && selectedDocs?.[0]?.id) {
        releaseMikserRowLock(selectedDocs[0].id).catch(() => {});
      }
    };
    // eslint-disable-next-line
  }, [activePompaGroupKey, mode, selectedDocs]);

const allOps = ["Yusuf ÖCAL", "Ahmet YILMAZ", "Mehmet AY"]; // Burası örnek, datalist ID'si için hazırlık
const allYags = ["Sayit SALMAN", "Ali VELİ"];

  const selectFromResults = async (row) => {
    if (!row) return;

    // sadece tamamlandi true düzenlenebilir
    if (row.tamamlandi !== true) {
      alert("Bu kaydı düzeltmek için önce kaydı tamamlayın (tamamlandi:true olmalı).");
      return;
    }

    // soft silinmiş gelmiyor ama yine de güvenlik
    if (row.deleted === true) {
      alert("Bu kayıt soft silinmiş. Düzenlenemez.");
      return;
    }

    // kilit kontrol (genel)
    if (isRowLockedGeneric(row, email)) {
      alert("Bu kayıt kilitli (lock). Düzenlenemez.");
      return;
    }

    if (mode === "mikser") {
      // önce varsa eski lock'u bırak
      if (selectedDocs?.[0]?.id && selectedDocs[0].id !== row.id) {
        await releaseMikserRowLock(selectedDocs[0].id);
      }
      const ok = await acquireMikserRowLockIfNeeded(row.id);
      if (!ok) return;

      setSelectedDocs([{ ...row }]);
    } else {
      // pompa: irsaliyeden seçildiyse de aynı group'un tamamını çekip düzenletelim
      // (pompa sayfanız group mantığında olduğu için)
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_pompa_hakedis"),
          where("deleted", "==", false),
          where("pompaFirmaId", "==", INOSEL_ID),
          where("tarihStr", "==", row.tarihStr),
          where("pompaPlaka", "==", row.pompaPlaka),
          where("santiyeAdi", "==", row.santiyeAdi)
        )
      );
      const groupList = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

      // tamamlandi şartı: group içinde tamamlanmayan varsa bile, kullanıcı sadece tamamlanmışları düzeltmek istiyor.
      // Sen “düzeltme işlemi sadece tamamlandi:true kayıtlarda yapılabilsin” dediğin için,
      // group içinde tamamlandi:false varsa onları da düzenletmiyoruz; ama group listede kalabilir.
      const editable = groupList.filter((x) => x.tamamlandi === true && x.deleted !== true);

      if (!editable.length) {
        alert("Bu grup içinde düzeltmeye uygun (tamamlandi:true) kayıt yok.");
        return;
      }

      // group lock al
      const ok = await acquirePompaGroupLock(editable);
      if (!ok) return;

      // group defaultlarını ilk kayıttan set et
      const f = editable[0];
      setPompaGroup({
        baslangicSaat: f.baslangicSaat || "",
        bitisSaat: f.bitisSaat || "",
        operatorList: [f.operatorAdi || "", f.operatorAdi2 || "", f.operatorAdi3 || ""].filter(
          (_, i, arr) => i === 0 || arr.some((x) => asText(x))
        ).length
          ? [f.operatorAdi || "", f.operatorAdi2 || "", f.operatorAdi3 || ""]
          : [""],
        yagciList: [f.yagciAdi || "", f.yagciAdi2 || "", f.yagciAdi3 || ""].filter(
          (_, i, arr) => i === 0 || arr.some((x) => asText(x))
        ).length
          ? [f.yagciAdi || "", f.yagciAdi2 || "", f.yagciAdi3 || ""]
          : [""],
      });

      // ekranda group içi irsaliyeleri sıralı gösterelim
      const sorted = [...editable].sort((a, b) => {
        const aa = irsSuffixNum(a.irsaliyeNo) ?? 0;
        const bb = irsSuffixNum(b.irsaliyeNo) ?? 0;
        return aa - bb;
      });

      setSelectedDocs(sorted);
    }
  };

  /* ===================== EDIT STATE ===================== */

  const updateSelectedDocField = (docId, key, val) => {
    setSelectedDocs((prev) =>
      prev.map((d) => {
        if (d.id !== docId) return d;
        const next = { ...d };
        if (key.toLowerCase().includes("saat")) next[key] = formatTimeSmart(val);
        else next[key] = val;
        return next;
      })
    );
  };

  const updatePompaGroupField = (k, v) => {
    setPompaGroup((s) => {
      const next = { ...s };
      if (k === "baslangicSaat" || k === "bitisSaat") next[k] = formatTimeSmart(v);
      else next[k] = v;
      return next;
    });
  };

const setPompaListValue = (type, index, value) => {
    setPompaGroup((prev) => {
      const listKey = type === "operator" ? "operatorList" : "yagciList";
      const newList = [...prev[listKey]];
      newList[index] = value; // Sadece o kutunun değerini günceller
      return { ...prev, [listKey]: newList };
    });
  };

  /* ===================== VALIDATIONS ===================== */

  const validateMikserDoc = async (docObj) => {
    const errs = [];

    if (docObj.tamamlandi !== true) errs.push("Kayıt tamamlanmamış (tamamlandi:true olmalı).");

    const ck = safeNum(docObj.cikisKm);
    const dk = safeNum(docObj.donusKm);
    if (ck == null) errs.push("Çıkış KM boş/hatalı");
    if (dk == null) errs.push("Dönüş KM boş/hatalı");
    if (ck != null && dk != null && dk <= ck) errs.push("Dönüş KM, Çıkış KM’den büyük olmalı");

    const cs = parseHHMM(docObj.cikisSaat);
    const ds = parseHHMM(docObj.donusSaat);
    if (cs == null) errs.push("Çıkış saat boş/hatalı");
    if (ds == null) errs.push("Dönüş saat boş/hatalı");

    if (!asText(docObj.soforAdi)) errs.push("Şoför boş");

    // irsaliye duplicate kontrol (deleted:false)
    const irs = asText(docObj.irsaliyeNo).toUpperCase();
    if (!irs) errs.push("İrsaliye No boş");
    if (irs) {
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_mikser_sevkiyat"),
          where("deleted", "==", false),
          where("irsaliyeNo", "==", irs)
        )
      );
      const others = snap.docs.filter((d) => d.id !== docObj.id);
      if (others.length) errs.push("İrsaliye No sistemde zaten kayıtlı (başka kayıt var).");
    }

    // KM zinciri (aynı gün + aynı plaka) basit kontrol: irsaliye suffix sırası
    // Not: Zinciri günler arası da istersen burada genişletiriz.
    const p = asText(docObj.mikserPlaka).toUpperCase();
    const ts = asText(docObj.tarihStr);
    if (p && ts) {
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_mikser_sevkiyat"),
          where("deleted", "==", false),
          where("tarihStr", "==", ts),
          where("mikserPlaka", "==", p)
        )
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      const allSorted = [...all]
        .filter((x) => x.id !== docObj.id) // sonra ekleyeceğiz
        .concat([{ ...docObj }])
        .sort((a, b) => (irsSuffixNum(a.irsaliyeNo) ?? 0) - (irsSuffixNum(b.irsaliyeNo) ?? 0));

      const idx = allSorted.findIndex((x) => x.id === docObj.id);
      const prev = idx > 0 ? allSorted[idx - 1] : null;
      const next = idx >= 0 && idx < allSorted.length - 1 ? allSorted[idx + 1] : null;

      const curCikis = safeNum(docObj.cikisKm);
      const curDonus = safeNum(docObj.donusKm);

      if (prev) {
        const prevDonus = safeNum(prev.donusKm);
        if (prevDonus != null && curCikis != null && prevDonus > curCikis) {
          errs.push("KM zinciri: önceki irsaliyede Dönüş KM > bu irsaliyede Çıkış KM");
        }
      }
      if (next) {
        const nextCikis = safeNum(next.cikisKm);
        if (curDonus != null && nextCikis != null && curDonus > nextCikis) {
          errs.push("KM zinciri: bu irsaliyede Dönüş KM > sonraki irsaliyede Çıkış KM");
        }
      }
    }

    return errs;
  };

  const validatePompaDocs = async (docs, group) => {
    const errs = [];
    if (!docs?.length) return ["Seçili kayıt yok."];

    // group zorunlular
    if (parseHHMM(group.baslangicSaat) == null) errs.push("Baş. saat boş/hatalı");
    if (parseHHMM(group.bitisSaat) == null) errs.push("Bit. saat boş/hatalı");
    const opFilled = group.operatorList.some((x) => asText(x));
    const ygFilled = group.yagciList.some((x) => asText(x));
    if (!opFilled) errs.push("Operatör en az 1 kişi olmalı");
    if (!ygFilled) errs.push("Yağcı en az 1 kişi olmalı");

    // her irsaliyede fiiliM3
    for (const d of docs) {
      if (d.tamamlandi !== true) errs.push(`(${d.irsaliyeNo}) tamamlandi:true olmalı`);
      const fm3 = safeNum(d.fiiliM3);
      if (fm3 == null || fm3 <= 0) errs.push(`(${d.irsaliyeNo}) Fiili m3 boş/hatalı`);
    }

    // irsaliye duplicate (pompa koleksiyonu)
    const irsList = docs.map((x) => asText(x.irsaliyeNo).toUpperCase()).filter(Boolean);
    for (const irs of irsList) {
      const snap = await getDocs(
        query(collection(db, "hazirbeton_pompa_hakedis"), where("deleted", "==", false), where("irsaliyeNo", "==", irs))
      );
      const others = snap.docs.filter((d) => !docs.some((x) => x.id === d.id));
      if (others.length) errs.push(`(${irs}) İrsaliye No sistemde başka kayıtta da var.`);
    }

    return errs;
  };

/* ===================== SAVE / DELETE ===================== */
  const handleSave = async () => {
    if (!selectedDocs.length) {
      alert("Önce kayıt seçiniz.");
      return;
    }

    // Genel Kontroller (Tamamlandı, Silinmiş, Kilit)
    if (selectedDocs.some((d) => d.tamamlandi !== true)) {
      alert("Sadece tamamlandi:true kayıtlar düzeltilebilir.");
      return;
    }
    if (selectedDocs.some((d) => d.deleted === true)) {
      alert("Silinmiş kayıtlar üzerinde işlem yapılamaz.");
      return;
    }
    if (selectedDocs.some((d) => isRowLockedGeneric(d, email))) {
      alert("Kayıt(lar) kilitli (lock). Düzenlenemez.");
      return;
    }

    setLoading(true);
    try {
      const now = new Date();
      const userMail = email || auth.currentUser?.email || "bilinmiyor";
      const batch = writeBatch(db);

      if (mode === "mikser") {
        // MİKSER GÜNCELLEME
        const d = selectedDocs[0];
        const errs = await validateMikserDoc(d);
        if (errs.length) {
          alert("Kaydetme engellendi:\n- " + errs.join("\n- "));
          setLoading(false);
          return;
        }

// --- 709. satır civarını şu şekilde güncelle ---
const before = results.find((x) => x.id === d.id) || {};

// keys listesine fiiliM3 ekledik (değişiklik geçmişinde görünmesi için)
const keys = ["irsaliyeNo", "mikserPlaka", "soforAdi", "fiiliM3", "cikisKm", "donusKm", "votoKm", "cikisSaat", "donusSaat", "aciklama"];
const changes = diffChanges(before, d, keys);

const docRef = doc(db, "hazirbeton_mikser_sevkiyat", d.id);
batch.update(docRef, {
  irsaliyeNo: asText(d.irsaliyeNo),
  mikserPlaka: formatPlate(d.mikserPlaka),
  soforAdi: asText(d.soforAdi),
  fiiliM3: safeNum(d.fiiliM3), // <-- EKSİK OLAN BUYDU, EKLEDİK
  cikisKm: safeNum(d.cikisKm),
  donusKm: safeNum(d.donusKm),
  votoKm: safeNum(d.votoKm) ?? 0,
  cikisSaat: asText(d.cikisSaat),
  donusSaat: asText(d.donusSaat),
  aciklama: asText(d.aciklama),
  updatedAt: serverTimestamp(),
  updatedBy: userMail,
  ...(hasChangeDetail(changes)
    ? { history: arrayUnion(historyEntry({ action: "update", by: userMail, changes })) }
    : {}),
});

      } else {
        // POMPA GÜNCELLEME (Grup Mantığı)
        const errs = await validatePompaDocs(selectedDocs, pompaGroup);
        if (errs.length) {
          alert("Kaydetme engellendi:\n- " + errs.join("\n- "));
          setLoading(false);
          return;
        }

        const op = [...pompaGroup.operatorList, "", "", ""].slice(0, 3);
        const yg = [...pompaGroup.yagciList, "", "", ""].slice(0, 3);

// --- 741. satır civarını şu şekilde güncelle ---
for (const docItem of selectedDocs) {
  const before = results.find((x) => x.id === docItem.id) || {};
  
  const guncelPompaPlaka = pompaGroup?.pompaPlaka || docItem.pompaPlaka || "";

  // Tüm kritik alanları takibe aldık (Voto KM hariç dediklerini de kapsar)
        const keys = [
          "fiiliM3",
          "irsaliyeNo",
          "mikserPlaka",
          "pompaPlaka",
          "baslangicSaat",
          "bitisSaat",
          "operatorAdi",
          "operatorAdi2",
          "operatorAdi3",
          "yagciAdi",
          "yagciAdi2",
          "yagciAdi3",
        ];
  
  // diffChanges için güncel durumu oluşturuyoruz
        const afterObj = {
          ...docItem,
          pompaPlaka: guncelPompaPlaka,
          baslangicSaat: pompaGroup.baslangicSaat,
          bitisSaat: pompaGroup.bitisSaat,
          operatorAdi: op[0] || "",
          operatorAdi2: op[1] || "",
          operatorAdi3: op[2] || "",
          yagciAdi: yg[0] || "",
          yagciAdi2: yg[1] || "",
          yagciAdi3: yg[2] || "",
        };
  
  const changes = diffChanges(before, afterObj, keys);

  const docRef = doc(db, "hazirbeton_pompa_hakedis", docItem.id);
  batch.update(docRef, {
    irsaliyeNo: asText(docItem.irsaliyeNo),
    mikserPlaka: formatPlate(docItem.mikserPlaka),
    pompaPlaka: formatPlate(guncelPompaPlaka),
    fiiliM3: safeNum(docItem.fiiliM3), // <-- VERİTABANINA YAZAN SATIR
          votoKm: safeNum(docItem.votoKm) ?? 0,
          baslangicSaat: asText(pompaGroup.baslangicSaat),
          bitisSaat: asText(pompaGroup.bitisSaat),
          operatorAdi: asText(op[0]),
          operatorAdi2: asText(op[1]),
          operatorAdi3: asText(op[2]),
          yagciAdi: asText(yg[0]),
          yagciAdi2: asText(yg[1]),
          yagciAdi3: asText(yg[2]),
          updatedAt: serverTimestamp(),
          updatedBy: userMail,
          ...(hasChangeDetail(changes)
            ? { history: arrayUnion(historyEntry({ action: "update", by: userMail, changes })) }
            : {}),
  });


          // 2. ÜRETİM (ZONE) GÜNCELLEME
          const qU = query(collection(db, "hazirbeton_zone_uretim"), where("irsaliyeNo", "==", docItem.irsaliyeNo));
          const snapU = await getDocs(qU);
          snapU.forEach((uDoc) => {
            batch.update(uDoc.ref, {
              pompaPlaka: formatPlate(afterObj.pompaPlaka),
              operatorAdi: op[0],
              updatedAt: serverTimestamp(),
              updatedBy: userMail + " (Sistem)"
            });
          });
        }
      }

      await batch.commit();
      alert("Başarıyla güncellendi ✅");
      resetSelection(); // Arama sonuçlarını ve seçimleri temizler
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      alert("Hata: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!selectedDocs.length) return;

    // Geri alınamaz
    const ok = window.confirm(
      "Bu kayıt(lar) soft silinecek. Bu ekrandan geri alınamaz.\n(Eğer geri almak gerekirse sadece veritabanından müdahale ile.)\nDevam edilsin mi?"
    );
    if (!ok) return;

    // tamamlandi + lock
    if (selectedDocs.some((d) => d.tamamlandi !== true)) {
      alert("Sadece tamamlandi:true kayıtlar silinebilir. (Kaydı tamamlayın)");
      return;
    }
    if (selectedDocs.some((d) => isRowLockedGeneric(d, email))) {
      alert("Kayıt(lar) kilitli (lock). Silinemez.");
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);

      for (const d of selectedDocs) {
        const ref = doc(db, targetCollection, d.id);
        batch.update(ref, {
          deleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: email || "anonim",
          history: arrayUnion(historyEntry({ action: "delete", by: email, changes: {} })),
        });
      }

      // kilit bırak (mikser/pompa)
      if (mode === "mikser" && selectedDocs?.[0]?.id) {
        batch.update(doc(db, "hazirbeton_mikser_sevkiyat", selectedDocs[0].id), {
          lock: { isOpen: false, openedBy: "", openedAt: null },
        });
      }
      if (mode === "pompa" && activePompaGroupKey) {
        // batch ile deleteField atmak için doc listesi lazım; burada kısa yoldan lock alanını deleteField ile temizliyoruz
        // (group içindeki her doc’a)
        for (const d of selectedDocs) {
          batch.update(doc(db, "hazirbeton_pompa_hakedis", d.id), { lock: deleteField() });
        }
      }

      await batch.commit();

      alert("Soft silindi 🗑️");

      // UI temizle
      resetSelection();
    } finally {
      setLoading(false);
    }
  };

  runSearchRef.current = runSearch;
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const isTypingTarget = (target) => {
      const tag = String(target?.tagName || "").toLowerCase();
      const type = String(target?.type || "").toLowerCase();
      return (tag === "input" && type !== "date") || tag === "textarea" || tag === "select" || target?.isContentEditable;
    };

    const onKeyDown = (e) => {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        spaceShortcutRef.current = true;
        return;
      }
      if (e.key !== "Enter" || isTypingTarget(e.target)) return;
      e.preventDefault();
      if (spaceShortcutRef.current) handleSaveRef.current?.();
      else runSearchRef.current?.();
    };

    const onKeyUp = (e) => {
      if (e.code === "Space") spaceShortcutRef.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  /* ===================== UI ===================== */

  const selectedTitle = useMemo(() => {
    if (!selectedDocs.length) return "";
    if (mode === "mikser") {
      const d = selectedDocs[0];
      return `${d.mikserPlaka || "-"} • ${d.tarihStr || "-"} • ${d.irsaliyeNo || "-"}`;
    }
    const d = selectedDocs[0];
    return `${d.pompaPlaka || "-"} • ${d.tarihStr || "-"} • ${d.santiyeAdi || "-"}`;
  }, [mode, selectedDocs]);

  const renderHistory = (docObj) => {
    const hist = Array.isArray(docObj?.history) ? docObj.history : [];
    if (!hist.length) return <div style={{ fontSize: 12, color: "#64748b" }}>History yok.</div>;

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", minWidth: 720, fontSize: 12 }}>
          <thead style={{ background: "#e2e8f0" }}>
            <tr>
              {["İşlem", "Kullanıcı", "Zaman", "Değişiklikler"].map((h) => (
                <th key={h} style={{ border: "1px solid #cbd5e1", padding: 6 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hist.map((h, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{h.action || "-"}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{h.by || "-"}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                  {h.at?.toDate ? h.at.toDate().toLocaleString("tr-TR") : "-"}
                </td>
                <td style={{ border: "1px solid #e5e7eb", padding: 6, whiteSpace: "pre-wrap" }}>
                  {hasChangeDetail(h.changes)
                    ? Object.entries(h.changes)
                        .map(([field, values]) => {
                          const before = Array.isArray(values) ? values[0] : "";
                          const after = Array.isArray(values) ? values[1] : values;
                          return `${field}: ${before || "-"} -> ${after || "-"}`;
                        })
                        .join("\n")
                    : "Detay yok"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const inputStyle = {
    border: "1px solid #cbd5e1",
    padding: 10,
    borderRadius: 6,
    width: "100%",
  };

  const btn = (bg) => ({
    border: "1px solid " + (bg === "#16a34a" ? "#15803d" : bg === "#dc2626" ? "#b91c1c" : "#cbd5e1"),
    background:
      bg === "#16a34a"
        ? "linear-gradient(180deg, #22c55e, #16a34a)"
        : bg === "#dc2626"
          ? "linear-gradient(180deg, #ef4444, #dc2626)"
          : "linear-gradient(180deg, #fff, #f8fafc)",
    color: bg === "#16a34a" || bg === "#dc2626" ? "#fff" : "#0f172a",
    padding: "9px 16px",
    borderRadius: 7,
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15,23,42,0.12)",
    opacity: loading ? 0.6 : 1,
  });

  return (
    <div style={{ padding: 20 }}>
<MyDataLists list={personeller} />
      <h2 style={{ color: "#0f172a", marginBottom: 12 }}>Sevkiyat Düzelt / Soft Sil</h2>

      {/* MODE */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            checked={mode === "mikser"}
            onChange={() => setMode("mikser")}
          />
          Mikser
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="radio" checked={mode === "pompa"} onChange={() => setMode("pompa")} />
          Pompa
        </label>

        <div style={{ flex: 1 }} />

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            checked={searchType === "irsaliye"}
            onChange={() => setSearchType("irsaliye")}
          />
          İrsaliye No ile
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="radio"
            checked={searchType === "plakaTarih"}
            onChange={() => setSearchType("plakaTarih")}
          />
          Plaka + Tarih ile
        </label>
      </div>

 {/* SEARCH BAR */}
{searchType === "irsaliye" ? (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "260px 110px",
      gap: 8,
      maxWidth: 980,
      marginBottom: 14,
      alignItems: "start",
    }}
  >
    <input
      value={irsaliyeNo}
      onChange={(e) => setIrsaliyeNo(e.target.value.toUpperCase())}
      placeholder="İrsaliye No"
      style={inputStyle}
    />
    <button
      type="button"
      onClick={runSearch}
      style={{ ...btn("#0ea5e9"), width: 110, height: 42 }}
      disabled={loading}
      title="Enter"
    >
      {loading ? "Yükleniyor..." : "Getir"}
    </button>
  </div>
) : (
  <div style={{ marginBottom: 14 }}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "150px 150px 110px",
        alignItems: "center",
        gap: 6,
        maxWidth: 430,
      }}
    >
      {/* PLAKA */}
      <>
        <input
          value={plaka}
          onChange={(e) => setPlaka(formatPlate(e.target.value))}
          placeholder={mode === "pompa" ? "Pompa Plaka" : "Mikser Plaka"}
          list="plateList"
          style={{
            height: 42,     // 🔥 Aynı yükseklik
            width: 150,
            padding: "0 10px",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            fontSize: 15,
            boxSizing: "border-box"
          }}
        />

        <datalist id="plateList">
          {plateOptions.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>


      {/* TARİH */}
        <input
          type="date"
          value={tarihStr}
          onChange={(e) => setTarihStr(e.target.value)}
          style={{
            height: 42,
            width: 150,
            padding: "0 10px",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            fontSize: 15,
            boxSizing: "border-box"
          }}
        />
      

      {/* BUTON */}
      <button
        type="button"
        onClick={runSearch}
        style={{ ...btn("#0ea5e9"), width: 110, height: 42, padding: "0 14px" }}
        disabled={loading}
        title="Enter"
      >
        {loading ? "Yükleniyor..." : "Getir"}
      </button>
      </>
    </div>
  </div>
)}

      

      {/* RESULTS */}
      {results.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ margin: "10px 0", color: "#0f172a" }}>Bulunan Kayıtlar</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: 980, fontSize: 13 }}>
              <thead style={{ background: "#e2e8f0" }}>
                <tr>
                  {[
                    "Seç",
                    "Tarih",
                    "İrsaliye",
                    mode === "mikser" ? "Mikser Plaka" : "Pompa Plaka",
                    "Şantiye",
                    "Tamamlandı?",
                    "Kilit?",
                  ].map((h) => (
                    <th key={h} style={{ border: "1px solid #cbd5e1", padding: 6 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const locked = isRowLockedGeneric(r, email);
                  const lockTitle = lockReasonText(r, email);
                  return (
                    <tr key={r.id} style={{ background: r.tamamlandi ? "#f8fafc" : "#fff7ed" }}>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6, textAlign: "center" }} title={lockTitle}>
                        <button
                          type="button"
                          onClick={() => selectFromResults(r)}
                          style={{
                            ...btn("#16a34a"),
                            padding: "6px 10px",
                            opacity: locked ? 0.5 : 1,
                          }}
                          disabled={loading}
                          title={locked ? "Kilitli" : "Seç"}
                        >
                          Seç
                        </button>
                      </td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{r.tarihStr || "-"}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{r.irsaliyeNo || "-"}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                        {mode === "mikser" ? r.mikserPlaka : r.pompaPlaka}
                      </td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{r.santiyeAdi || "-"}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6, textAlign: "center" }} title={lockTitle}>
                        {r.tamamlandi ? "✅" : "❌"}
                      </td>
                      <td style={{ border: "1px solid #e5e7eb", padding: 6, textAlign: "center" }} title={lockTitle}>
                        {locked ? "🔒" : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SELECTED EDIT */}
      {selectedDocs.length > 0 && (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Seçili: {selectedTitle}</h3>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={handleSave} style={btn("#16a34a")} disabled={loading} title="Space + Enter">
              Kaydet
            </button>
            <button type="button" onClick={handleSoftDelete} style={btn("#dc2626")} disabled={loading}>
              Soft Sil
            </button>
          </div>

{mode === "mikser" ? (
  <div style={{ marginTop: "20px" }}>
    <div style={{ 
      overflowX: "auto", 
      background: "#fff", 
      borderRadius: "12px", 
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" 
    }}>
      {/* tableLayout: "auto" yaptık ki içerik sığmazsa tabloyu yana doğru açsın, ezmesin */}
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "max-content", tableLayout: "auto" }}>
        <thead style={{ background: "#f8fafc" }}>
          <tr>
            {[
              "İrsaliye No", "Mikser Plaka", "Şoför", "Gelen/Fiili", 
              "Çıkış KM", "Dönüş KM", "Voto KM", "Saatler", "Açıklama", "Geçmiş"
            ].map((h) => (
              <th key={h} style={{ padding: "12px", textAlign: "left", fontSize: "13px", color: "#64748b", borderBottom: "2px solid #f1f5f9", fontWeight: "600" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {selectedDocs.map((d) => (
            <tr key={d.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              {/* Sütunlarda width yerine minWidth kullanarak "yarısı kaybolmasın" kuralını uyguluyoruz */}
              <td style={{ padding: "12px", fontWeight: "700", color: "#1e40af", minWidth: "110px" }}>
                {d.irsaliyeNo}
              </td>
              
              <td style={{ padding: "12px", minWidth: "140px" }}>
                <input
                  value={d.mikserPlaka || ""}
                  onChange={(e) => updateSelectedDocField(d.id, "mikserPlaka", e.target.value.toUpperCase())}
                  style={{ ...modernInput, height: "36px", width: "100%" }}
                  list="plateList"
                />
              </td>

              <td style={{ padding: "12px", minWidth: "160px" }}>
                <input
                  value={d.soforAdi || ""}
                  onChange={(e) => updateSelectedDocField(d.id, "soforAdi", e.target.value)}
                  style={{ ...modernInput, height: "36px", width: "100%" }}
                  list="personel-listesi"
                  placeholder="Şoför..."
                />
              </td>

              <td style={{ padding: "12px", minWidth: "120px" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Gelen: {d.m3} m³</div>
                  <input
                  type="number"
                  value={d.fiiliM3 ?? ""}
                  onChange={(e) => updateSelectedDocField(d.id, "fiiliM3", e.target.value)}
                  style={{ ...modernInput, height: "32px", textAlign: "center", fontWeight: "bold", color: "#059669", marginTop: "4px", width: "100%" }}
                  placeholder="Fiili"
                />
              </td>

              <td style={{ padding: "12px", minWidth: "90px" }}>
                <input type="number" value={d.cikisKm ?? ""} onChange={(e) => updateSelectedDocField(d.id, "cikisKm", e.target.value)} style={{ ...modernInput, height: "32px", width: "100%" }} placeholder="Çıkış" />
              </td>
              <td style={{ padding: "12px", minWidth: "90px" }}>
                <input type="number" value={d.donusKm ?? ""} onChange={(e) => updateSelectedDocField(d.id, "donusKm", e.target.value)} style={{ ...modernInput, height: "32px", width: "100%" }} placeholder="Dönüş" />
              </td>
              <td style={{ padding: "12px", minWidth: "80px" }}>
                <input type="number" value={d.votoKm ?? 0} onChange={(e) => updateSelectedDocField(d.id, "votoKm", e.target.value)} style={{ ...modernInput, height: "32px", textAlign: "center", width: "100%" }} />
              </td>

              <td style={{ padding: "12px", minWidth: "150px" }}>
                <div style={{ display: "flex", gap: "4px" }}>
                  <input value={d.cikisSaat || ""} onChange={(e) => updateSelectedDocField(d.id, "cikisSaat", e.target.value)} style={{ ...modernInput, height: "32px", width: "50%" }} placeholder="Çıkış" />
                  <input value={d.donusSaat || ""} onChange={(e) => updateSelectedDocField(d.id, "donusSaat", e.target.value)} style={{ ...modernInput, height: "32px", width: "50%" }} placeholder="Dönüş" />
                </div>
              </td>

              <td style={{ padding: "12px", minWidth: "150px" }}>
                <input
                  value={d.aciklama || ""}
                  onChange={(e) => updateSelectedDocField(d.id, "aciklama", e.target.value)}
                  style={{ ...modernInput, height: "36px", width: "100%" }}
                  placeholder="Not..."
                />
              </td>

              {/* Geçmiş Hücresi - Tam Pompa Mantığı */}
              <td style={{ 
                padding: "12px 8px", 
                minWidth: "350px", 
                verticalAlign: "top" 
              }}>
                <div style={{ 
                  fontSize: "11px", 
                  color: "#64748b",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  height: "auto",
                  maxHeight: "none",
                  overflow: "visible",
                  display: "block"
                }}>
                  {renderHistory(d)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
) : (
  // Burası Pompa Kısmı olarak devam ediyor...

  <>

{/* SEÇİLİ KAYITLARI DÜZENLEME PANELİ */}
          <div style={cardStyle}>
            <h3 style={{ margin: "0 0 15px 0", color: "#1e40af", fontSize: "16px", borderBottom: "2px solid #3b82f6", paddingBottom: "5px" }}>
              Grup Bilgilerini Güncelle
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
              
              {/* PLAKA - TAM GENİŞLİK */}
              <div style={{ gridColumn: "1 / span 2", padding: "12px", background: "#eff6ff", borderRadius: "10px", border: "1px solid #bfdbfe" }}>
                <div style={{ fontSize: "12px", color: "#1e40af", fontWeight: "bold", marginBottom: "5px" }}>Pompa Plaka</div>
                <input
                  value={selectedDocs[0]?.pompaPlaka || ""}
                  onChange={(e) => {
                    const val = formatPlate(e.target.value); 
                    selectedDocs.forEach(d => updateSelectedDocField(d.id, "pompaPlaka", val));
                  }}
                  style={{ ...modernInput, borderColor: "#3b82f6", fontWeight: "bold", fontSize: "16px" }}
                  list="plateList"
                  placeholder="Plaka seçiniz..."
                />
              </div>

              {/* SAATLER */}
              <div>
                <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "bold" }}>Başlangıç Saat</div>
                <input 
                  type="time"
                  value={pompaGroup.baslangicSaat} 
                  onChange={(e) => updatePompaGroupField("baslangicSaat", e.target.value)} 
                  style={modernInput} 
                />
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "bold" }}>Bitiş Saat</div>
                <input 
                  type="time"
                  value={pompaGroup.bitisSaat} 
                  onChange={(e) => updatePompaGroupField("bitisSaat", e.target.value)} 
                  style={modernInput} 
                />
              </div>

              {/* OPERATÖRLER */}
              <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "13px", color: "#2563eb", fontWeight: "bold", marginBottom: "8px" }}> Operatörler</div>
                {pompaGroup.operatorList.map((op, idx) => (
                  <input
                    key={`op-${idx}`}
                    value={op || ""}
                    onChange={(e) => setPompaListValue("operator", idx, e.target.value)}
                    style={{ ...modernInput, marginBottom: "5px" }}
                    list="personel-listesi"
                    placeholder="Operatör adı..."
                  />
                ))}
              </div>

              {/* YAĞCILAR */}
              <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "13px", color: "#059669", fontWeight: "bold", marginBottom: "8px" }}> Yağcılar</div>
                {pompaGroup.yagciList.map((yg, idx) => (
                  <input
                    key={`yg-${idx}`}
                    value={yg || ""}
                    onChange={(e) => setPompaListValue("yagci", idx, e.target.value)}
                    style={{ ...modernInput, marginBottom: "5px" }}
                    list="personel-listesi"
                    placeholder="Yağcı adı..."
                  />
                ))}
              </div>

            </div>
          </div>

{/* POMPA DOCS TABLE */}
<div style={{ marginTop: 25 }}>
  <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
    <span style={{ background: "#3b82f6", width: "4px", height: "18px", borderRadius: "2px" }}></span>
    İrsaliye Bazlı Kayıtlar
  </h4>
  
  <div style={{ 
    overflowX: "auto", 
    background: "#fff", 
    borderRadius: "12px", 
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)" 
  }}>
    <table style={{ borderCollapse: "collapse", minWidth: 1100, fontSize: 13, width: "100%" }}>
      <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
        <tr>
          {["İrsaliye", "Mikser Plaka", "Gelen m3", "Fiili m3", "Voto KM", "Geçmiş"].map((h) => (
            <th key={h} style={{ borderBottom: "2px solid #e2e8f0", padding: "12px 8px", textAlign: "left", color: "#64748b" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {selectedDocs.map((d) => (
          <tr key={d.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
            <td style={{ padding: "12px 8px", fontWeight: "bold" }}>{d.irsaliyeNo || "-"}</td>
            <td style={{ padding: "12px 8px" }}>
              <input
                value={d.mikserPlaka || ""}
                onChange={(e) => updateSelectedDocField(d.id, "mikserPlaka", e.target.value.toUpperCase())}
                style={{ ...modernInput, height: "32px", fontSize: "13px" }}
              />
            </td>
            <td style={{ padding: "12px 8px" }}>{d.m3 ?? "-"}</td>
            <td style={{ padding: "12px 8px" }}>
              <input
                value={d.fiiliM3 ?? ""}
                onChange={(e) => updateSelectedDocField(d.id, "fiiliM3", e.target.value)}
                style={{ ...modernInput, height: "32px" }}
              />
            </td>
            <td style={{ padding: "12px 8px" }}>
              <input
                value={d.votoKm ?? 0}
                onChange={(e) => updateSelectedDocField(d.id, "votoKm", e.target.value)}
                style={{ ...modernInput, height: "32px" }}
              />
            </td>
            <td style={{ padding: "12px 8px", minWidth: 400 }}>
              {renderHistory(d)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>
 
      
            </>
          )}
        </div>
      )}




    </div>
  );
}

/* ================= PERSONEL LIST (AUTOCOMPLETE) ================= */
export const usePersonelList = () => {
  const [personeller, setPersoneller] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "personeller"));
        const list = snap.docs
          .map((d) => d.data())
          .filter((p) => p?.ad)
          .map((p) => `${p.ad}${p.soyad ? " " + p.soyad : ""}`)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "tr"));
        setPersoneller(list);
      } catch (e) {
        console.error("Personel listesi yüklenirken hata:", e);
      }
    })();
  }, []);
  return personeller;
};

const MyDataLists = ({ list }) => (
  <datalist id="personel-listesi">
    {(list || []).map((isim, i) => (
      <option key={i} value={isim} />
    ))}
  </datalist>
);

const validateMikserDoc = async (doc) => {
  const errs = [];
  if (!doc.irsaliyeNo) errs.push("İrsaliye No boş olamaz.");
  if (!doc.mikserPlaka) errs.push("Mikser Plaka boş olamaz.");
  return errs;
};
